import React, { useState, useCallback } from "react";
import { useRoleVerify } from "Common/RoleVerify";
import { GetAll } from "adminApi/CustomersApi/CustomerRefund";
import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import RefundModal from "./RefundModal";
import RefundHistoryModal from "./RefundHistoryModal";

/**
 * CustomerRefundTable
 * Paginated table listing all customers with an available balance > 0.
 * Each row has a "Process Refund" action button that opens RefundModal.
 */
const CustomerRefundTable = () => {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, 500);
  const sizeOptions = [10, 20, 50, 100];

  // Modal state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // add state alongside selectedCustomer/showModal
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const openHistory = useCallback((customer) => { setHistoryCustomer(customer); setShowHistory(true); }, []);
  const closeHistory = useCallback(() => { setShowHistory(false); setHistoryCustomer(null); }, []);

  const canCreate = useRoleVerify("", ["can_create"]);

  // ── Data fetch ───────────────────────────────────────────────────────────
  const { data, meta, loading, error, refetch } = usePaginatedFetch(GetAll, {
    page,
    limit,
    search: debouncedSearch,
    title: "CustomerRefund",
  });

  // ── Open / close modal ───────────────────────────────────────────────────
  const openModal = useCallback((customer) => {
    setSelectedCustomer(customer);
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setSelectedCustomer(null);
  }, []);

  const handleRefundSuccess = useCallback(() => {
    refetch();
    closeModal();
  }, [refetch, closeModal]);

  // ── Table columns ────────────────────────────────────────────────────────
  const columns = [
    { Header: "#", accessor: "sno", disableSortBy: true },
    { Header: "Customer Name", accessor: "customer_name" },
    { Header: "Email", accessor: "email" },
    { Header: "Phone", accessor: "customer_phone" },
    {
      Header: "Available Balance",
      accessor: "available_balance",
      Cell: ({ value }) => (
        <span
          className="badge fw-semibold px-3 py-2"
          style={{
            background: "#f0fff4",
            color: "#198754",
            border: "1px solid #b2f2bb",
            borderRadius: 20,
            fontSize: 13,
          }}
        >
          ₹{" "}
          {parseFloat(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    ...(
      [
        {
          Header: "Action",
          accessor: "action",
          disableSortBy: true,
          Cell: ({ row }) => (
            <div className="d-flex gap-2 w-100">
              {
                canCreate && (
                  <button
                    type="button"
                    className="btn btn-sm fw-semibold px-3 text-white d-flex align-items-center gap-1 btn-success"
                    style={{
                      border: "none",
                      borderRadius: 8,
                      fontSize: 13,
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => openModal(row.original)}
                    title="Process Refund"
                  >
                    <i className="ti ti-rotate-clockwise-2" style={{ fontSize: 14 }} />
                    Refund
                  </button>
                )
              }

                  <button
                    type="button"
                    className="btn btn-sm fw-semibold px-3 text-white d-flex align-items-center gap-1 btn-primary"
                    style={{
                      border: "none",
                      borderRadius: 8,
                      fontSize: 13,
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => openHistory(row.original)}
                    title="View History"
                  >
                    <i className="ti ti-history" style={{ fontSize: 14 }} />
                    History
                  </button>

            </div>
          ),
        },
      ]),
  ];

  // ── Build formatted rows (add serial number) ─────────────────────────────
  const formattedData = (data || []).map((row, idx) => ({
    ...row,
    sno: (page - 1) * limit + idx + 1,
  }));

  // ── Search / page handlers ───────────────────────────────────────────────
  const handleSearch = (val, resetPage = null) => {
    setSearch(val);
    if (resetPage !== null) setPage(resetPage);
  };
  const onPageChange = (p) => setPage(p);
  const onLimitChange = (n) => { setLimit(n); setPage(1); };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <DataTable
        columns={columns}
        data={formattedData}
        options={{
          loading,
          error,
          sortableColumns: ["customer_name", "email", "customer_phone", "available_balance"],
          copy: true,
          csv: true,
          pdf: true,
          print: true,
          fileName: "CustomerRefunds",
        }}
        Search={{
          search: true,
          SearchInputValue: search,
          handleSearch,
        }}
        Entries={{
          Entries: true,
          limit,
          onLimitChange,
          options: sizeOptions,
        }}
        pagination={{
          isActive: true,
          meta,
          onPageChange,
        }}
      />

      {/* Refund Modal */}
      {
        canCreate && (
          <RefundModal
            customer={selectedCustomer}
            show={showModal}
            onClose={closeModal}
            onSuccess={handleRefundSuccess}
          />
        )
      }




      <RefundHistoryModal
        customer={historyCustomer}
        show={showHistory}
        onClose={closeHistory}
      />
    </>
  );
};

export default CustomerRefundTable;
